import React, { useEffect, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import localeText from "../../../../../../../locale";
import { MAX_ATTRIBUTES, MAX_ATTRIBUTE_VALUES } from "../constants";
import { createLocalId } from "../utils";

function VariationAttributeEditor({ attributes, onChange, editingIndex, setEditingIndex }) {
  const [optionName, setOptionName] = useState("");
  const [optionValue, setOptionValue] = useState("");

  useEffect(() => {
    if (editingIndex == null) return;
    setOptionName(attributes[editingIndex]?.name || "");
    setOptionValue("");
  }, [editingIndex]);

  const persistCurrentName = (list = attributes) => {
    if (editingIndex == null || !list[editingIndex]) return list;
    return list.map((attr, index) =>
      index === editingIndex ? { ...attr, name: optionName.trim() || attr.name } : attr,
    );
  };

  const startNew = () => {
    if (attributes.length >= MAX_ATTRIBUTES) return;
    const persisted = persistCurrentName();
    const next = [...persisted, { localId: createLocalId(), name: "", values: [] }];
    onChange(next);
    setOptionName("");
    setOptionValue("");
    setEditingIndex(next.length - 1);
  };

  const saveCurrent = () => {
    if (!optionName.trim()) return;
    const current = attributes[editingIndex];
    if (!(current?.values || []).length) return;
    onChange(persistCurrentName());
    setEditingIndex(null);
  };

  const addValue = () => {
    const value = optionValue.trim();
    if (!value || editingIndex == null) return;
    const next = [...attributes];
    const current = next[editingIndex] || {
      localId: createLocalId(),
      name: optionName.trim(),
      values: [],
    };
    if ((current.values || []).length >= MAX_ATTRIBUTE_VALUES) return;
    if ((current.values || []).some((item) => item.value.toLowerCase() === value.toLowerCase())) {
      setOptionValue("");
      return;
    }
    current.values = [...(current.values || []), { localId: createLocalId(), value }];
    current.name = optionName.trim() || current.name;
    next[editingIndex] = current;
    onChange(next);
    setOptionValue("");
  };

  const removeAttribute = (index) => {
    onChange(attributes.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (editingIndex != null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const removeValue = (attrIndex, valueIndex) => {
    onChange(
      attributes.map((attr, i) =>
        i === attrIndex
          ? { ...attr, values: attr.values.filter((_, vi) => vi !== valueIndex) }
          : attr,
      ),
    );
  };

  const canFinish =
    Boolean(optionName.trim()) && Boolean((attributes[editingIndex]?.values || []).length);

  return (
    <div>
      {attributes.map((attr, index) =>
        editingIndex === index ? null : (
          <div
            key={attr.localId || index}
            className="product-form-attribute-card flex justify-content-between align-items-center gap-3"
          >
            <div>
              <p className="m-0 font-semibold">{attr.name}</p>
              <div className="product-form-variant-tags mt-2">
                {(attr.values || []).map((value) => (
                  <span key={value.localId || value.value} className="product-form-tag">
                    {value.value}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                icon="pi pi-pencil"
                className="p-button-text p-button-rounded"
                onClick={() => setEditingIndex(index)}
              />
              <Button
                type="button"
                icon="pi pi-trash"
                className="p-button-text p-button-rounded p-button-danger"
                onClick={() => removeAttribute(index)}
              />
            </div>
          </div>
        ),
      )}

      {editingIndex != null && (
        <div className="product-form-attribute-card">
          <div className="mb-3">
            <label className="product-form-label">
              {localeText("optionName")}
              <span className="product-form-required">*</span>
            </label>
            <InputText
              value={optionName}
              onChange={(e) => setOptionName(e.target.value)}
              className="w-full border-round-lg"
            />
          </div>
          <div>
            <label className="product-form-label">
              {localeText("optionValues")}
              <span className="product-form-required">*</span>
            </label>
            <div className="flex gap-2">
              <InputText
                value={optionValue}
                onChange={(e) => setOptionValue(e.target.value)}
                className="w-full border-round-lg"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addValue();
                  }
                }}
              />
              <Button
                type="button"
                label={localeText("addValue")}
                className="product-form-add-value border-round-lg text-sm font-medium"
                disabled={!optionValue.trim()}
                onClick={addValue}
              />
            </div>
          </div>
          <div className="product-form-variant-tags mt-3">
            {(attributes[editingIndex]?.values || []).map((value, valueIndex) => (
              <span key={value.localId || value.value} className="product-form-tag">
                {value.value}
                <button
                  type="button"
                  className="product-form-tag-remove"
                  onClick={() => removeValue(editingIndex, valueIndex)}
                  aria-label={localeText("remove")}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex md:flex-row flex-column md:justify-content-between justify-content-start mt-3 gap-2">
            <Button
              type="button"
              label={localeText("remove")}
              className="p-button-outlined p-button-danger border-round-lg text-sm font-medium"
              onClick={() => removeAttribute(editingIndex)}
            />
            <Button
              type="button"
              label={localeText("done")}
              disabled={!canFinish}
              onClick={saveCurrent}
              className="border-round-lg text-sm font-medium"
              style={{ backgroundColor: "var(--theme-secondary)" }}
            />
          </div>
        </div>
      )}

      {attributes.length < MAX_ATTRIBUTES && (
        <button type="button" className="product-form-link mt-2 flex align-items-center gap-2 text-xs" onClick={startNew}>
          <i className="pi pi-plus-circle" />
          <span>{localeText("addAnotherOption")}</span>
        </button>
      )}
    </div>
  );
}

export default VariationAttributeEditor;
